import { useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { MDC_DEGREES, THRESHOLDS, TIMING } from '@erectus/shared';

/** Stored buckets were 15 min before 2026-10-16 and 5 after; tell them apart by count. */
function legacyBucketSec(summary: SessionSummary): number {
  return summary.decay.length === Math.ceil(summary.durationSec / TIMING.decayBucketSec) ? TIMING.decayBucketSec : 15 * 60;
}
import type { Exercise, FrameRecord, SessionSummary } from '@erectus/shared';
import { AccuracyNote, Card, ErrorState, SectionTitle, Spinner } from '@/ui/primitives.js';
import { BackLink, PageHeader } from '@/ui/Layout.js';
import { Icon } from '@/ui/icons.js';
import { MetricBar, ScoreDial } from '@/ui/StateBadge.js';
import { useAuth } from '@/stores/auth.js';
import { getSession, listExercises } from '@/lib/repo.js';
import { localSummary } from '@/session/finalize.js';
import { loadFrames } from '@/session/db.js';
import { topEpisodes } from '@/scoring/sessionScore.js';
import { track } from '@/lib/analytics.js';
import { BLOCK_MIN, DecayBars, EpisodeList, Timeline, blocksFromSeries, formatMinute } from './Timeline.js';
import { SinceLastCheck } from './SinceLastCheck.js';
import { SittingCriteria } from './SittingCriteria.js';
import { CoachingCard } from './CoachingCard.js';
import { FeedbackWidget } from './FeedbackWidget.js';
import { ThroughSession } from './ThroughSession.js';
import { DeleteSession } from './DeleteSession.js';
import { hasSharedFrames, loadSharedFrames } from './sharedFrames.js';

/**
 * Session report (docs/PRODUCT.md §4).
 *
 * The summary is read from Firestore, falling back to the local copy so an
 * offline session still produces a report. Frame detail comes from IndexedDB,
 * because frames are never uploaded to Firestore.
 */
export function ReportPage(): ReactNode {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { user, userDoc } = useAuth();
  const [summary, setSummary] = useState<SessionSummary | null>(null);
  const [frames, setFrames] = useState<FrameRecord[]>([]);
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [status, setStatus] = useState<'loading' | 'ready' | 'missing'>('loading');
  // Not on this phone but shared to the account (opted in): the same detail from that file.
  const [accountFrames, setAccountFrames] = useState<FrameRecord[] | null>(null);
  const optedIn = userDoc?.consent?.dataOptIn === true;
  const roll = summary?.phoneRollDeg ?? null;

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const load = async (): Promise<void> => {
      const remote = await getSession(sessionId).catch(() => null);
      const local = remote ?? (await localSummary(sessionId).catch(() => null));
      const frameRows = await loadFrames(sessionId).catch(() => []);
      const exerciseRows = await listExercises().catch(() => []);
      if (cancelled) return;
      setSummary(local);
      setFrames(frameRows);
      setExercises(exerciseRows);
      setStatus(local ? 'ready' : 'missing');
      if (local) track('report_view', { has_coaching: true });
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  useEffect(() => {
    if (status !== 'ready' || frames.length > 0 || !optedIn || !user || !sessionId) return;
    let cancelled = false;
    void hasSharedFrames(user.uid, sessionId)
      .then((exists) => (exists ? loadSharedFrames(user.uid, sessionId, roll) : null))
      .then((rows) => {
        if (!cancelled && rows) setAccountFrames(rows);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [status, frames.length, optedIn, user, sessionId, roll]);

  if (status === 'loading') {
    return (
      <div className="grid min-h-60 place-items-center">
        <Spinner label="Building your report" />
      </div>
    );
  }

  if (status === 'missing' || !summary || !sessionId) {
    return (
      <ErrorState
        title="Report not found"
        message="This session is not on this device and not in your account."
        action={
          <Link className="text-sm font-semibold underline" to="/capture">
            Start a new session
          </Link>
        }
      />
    );
  }

  const unmeasured = Math.round((1 - summary.validRatio) * 100);
  const detail = frames.length > 0 ? frames : (accountFrames ?? []);
  // Older sessions without a per-minute series fall back to their stored buckets.
  const blocks = blocksFromSeries(summary.series, summary.durationSec);
  const durationMin = summary.durationSec / 60;

  return (
    <div className="space-y-4">
      <BackLink home="progress" />
      <PageHeader
        title="Session report"
        subtitle={`${new Date(summary.startedAt).toLocaleString()} · ${durationMin.toFixed(0)} min`}
      />

      <Card className="space-y-4">
        <ScoreDial score={summary.score} />
        <dl className="grid grid-cols-2 gap-3 text-sm">
          <Stat label="Good posture" value={`${Math.round(summary.goodRatio * 100)}%`} />
          <Stat
            label="First slip"
            value={summary.timeToFirst === null ? 'Never' : formatMinute(summary.timeToFirst)}
          />
          <Stat label="Head forward" value={`${summary.fhpMin.toFixed(0)} min`} />
          <Stat label="Slouching" value={`${summary.slouchMin.toFixed(0)} min`} />
          <Stat label="Leaning back" value={`${summary.reclineMin.toFixed(0)} min`} />
          <Stat label="Not measurable" value={`${unmeasured}%`} />
        </dl>
        <button
          type="button"
          className="flex w-full items-center justify-center gap-2 rounded-xl border border-line py-2.5 text-sm font-semibold"
          onClick={() => document.getElementById('through')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
        >
          <Icon name="play" size={16} />
          Skeleton replay and snapshots
        </button>
        {unmeasured >= 20 ? (
          <p className="text-xs text-[color:var(--color-state-fhp)]">
            {unmeasured}% of this session could not be measured. Check the camera angle, your ear
            visibility and the lighting next time.
          </p>
        ) : null}
      </Card>

      {user ? (
        <CoachingCard
          uid={user.uid}
          sessionId={sessionId}
          summary={summary}
          userDoc={userDoc}
          exercises={exercises}
        />
      ) : null}

      <Card>
        <SectionTitle>Posture over time</SectionTitle>
        <p className="mb-2 text-xs text-muted">
          The whole session from start to end, left to right, coloured by what was measured at each
          moment. Faded means you were out of view.
        </p>
        <Timeline frames={detail} durationSec={summary.durationSec} />
      </Card>

      <ThroughSession sessionId={sessionId} summary={summary} frames={frames} />

      <Card>
        <SectionTitle>{blocks ? `${BLOCK_MIN}-minute blocks` : 'Blocks'}</SectionTitle>
        <DecayBars
          decay={blocks ?? summary.decay}
          durationSec={summary.durationSec}
          blockSec={blocks ? BLOCK_MIN * 60 : legacyBucketSec(summary)}
        />
      </Card>

      <SinceLastCheck sessionId={sessionId} summary={summary} />

      <Card>
        <SectionTitle>Longest episodes</SectionTitle>
        <EpisodeList episodes={topEpisodes(summary.episodes)} />
      </Card>

      <Card>
        <SectionTitle>Angles</SectionTitle>
        <MetricBar
          label="Neck angle (CVA)"
          value={summary.cva?.mean ?? null}
          unit="°"
          min={20}
          max={90}
          goodFrom={THRESHOLDS.cva.good}
          goodTo={90}
        />
        <MetricBar
          label="Head tilt (CHA)"
          value={summary.cha?.mean ?? null}
          unit="°"
          min={-30}
          max={40}
          goodFrom={-30}
          goodTo={THRESHOLDS.cha.max}
        />
        <MetricBar
          label="Trunk lean"
          value={summary.trunk?.mean ?? null}
          unit="°"
          min={-30}
          max={45}
          goodFrom={THRESHOLDS.trunkInclination.goodMin}
          goodTo={THRESHOLDS.trunkInclination.goodMax}
        />
        <MetricBar
          label="Shoulder forward"
          value={summary.prot?.mean ?? null}
          unit=""
          min={-0.1}
          max={0.5}
          goodFrom={-0.1}
          goodTo={THRESHOLDS.shoulderProtraction.good}
          invert
        />
        <MetricBar
          label="Hip angle"
          value={summary.hip?.mean ?? null}
          unit="°"
          min={60}
          max={140}
          goodFrom={THRESHOLDS.hipAngle.goodMin}
          goodTo={THRESHOLDS.hipAngle.goodMax}
        />
        <div className="mt-2 space-y-1">
          <AccuracyNote />
          <p className="text-xs text-muted">
            A week-to-week change smaller than ±{MDC_DEGREES}° is shown as "maintained", because it
            is within measurement error.
          </p>
        </div>
      </Card>

      <SittingCriteria />

      {user ? (
        <FeedbackWidget
          uid={user.uid}
          sessionId={sessionId}
          frames={detail}
          durationSec={summary.durationSec}
        />
      ) : null}

      <Card className="text-xs text-muted">
        <p>
          Read {summary.sampleFps} times a second · camera placement{' '}
          {Math.round(summary.framingScore * 100)}% of an ideal side view · method {summary.thresholdsVersion}
        </p>
      </Card>

      <DeleteSession sessionId={sessionId} />

    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div>
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="text-base font-semibold">{value}</dd>
    </div>
  );
}
